import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ticket, Plus, LogOut, Bell, User, CreditCard, Settings } from 'lucide-react';
import CreateTicketModal from '@/components/CreateTicketModal';
import ReviewReferralCard from '@/components/ReviewReferralCard';
import { apiFetch, clearStoredToken } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [services, setServices] = useState([]);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [userRes, ticketsRes, notifRes, servicesRes] = await Promise.all([
        apiFetch(`${API}/auth/me`, { silent: true }),
        apiFetch(`${API}/tickets?skip=0&limit=50`),
        apiFetch(`${API}/notifications`),
        apiFetch(`${API}/services`)
      ]);

      if (userRes.ok) setUser(await userRes.json());
      if (ticketsRes.ok) {
        const data = await ticketsRes.json();
        setTickets(data.tickets || data);
      }
      if (notifRes.ok) {
        const data = await notifRes.json();
        setNotifications(data.notifications || data);
      }
      if (servicesRes.ok) {
        const data = await servicesRes.json();
        setServices(data.services || data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await apiFetch(`${API}/auth/logout`, {
      method: 'POST',
      silent: true,
    });
    clearStoredToken();
    navigate('/login');
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'text-[#00f0ff] border-[#00f0ff]',
      in_progress: 'text-[#ffa500] border-[#ffa500]',
      resolved: 'text-[#00ff00] border-[#00ff00]',
      closed: 'text-gray-400 border-gray-400'
    };
    return colors[status] || colors.open;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-[#00ff00]',
      medium: 'text-[#ffa500]',
      high: 'text-[#ff0000]'
    };
    return colors[priority] || colors.medium;
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00f0ff] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#00f0ff] glow">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="grid-background absolute inset-0 opacity-20"></div>
      
      {/* Header */}
      <nav className="relative z-10 border-b border-[#00f0ff]/30 bg-[#0a0a1f]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <div className="text-3xl glow">⚡</div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-[#00f0ff] via-[#ff00ff] to-[#b026ff] text-transparent bg-clip-text">
                Next Wave Tech Help
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-[#00f0ff] hover:text-[#ff00ff] transition-colors"
                data-testid="notifications-button"
              >
                <Bell size={24} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#ff00ff] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center glow">
                    {unreadCount}
                  </span>
                )}
              </button>
              <div className="flex items-center space-x-3">
                {user?.picture && (
                  <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full border-2 border-[#00f0ff] glow-box" />
                )}
                <div>
                  <p className="text-[#00f0ff] font-semibold">{user?.name}</p>
                  <p className="text-[#00f0ff]/60 text-sm">{user?.email}</p>
                </div>
              </div>
              {user?.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  className="px-4 py-2 border border-[#ff00ff] text-[#ff00ff] rounded-lg hover:bg-[#ff00ff] hover:text-white transition-all"
                  data-testid="admin-dashboard-button"
                >
                  Admin Dashboard
                </button>
              )}
              <button
                onClick={() => navigate('/account/change-password')}
                className="px-4 py-2 border border-[#00f0ff] text-[#00f0ff] rounded-lg hover:bg-[#00f0ff] hover:text-[#0a0a1f] transition-all"
                data-testid="change-password-link-button"
              >
                Change Password
              </button>
              <button
                onClick={handleLogout}
                className="p-2 text-[#ff00ff] hover:text-[#ff0080] transition-colors"
                data-testid="logout-button"
              >
                <LogOut size={24} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Notifications Dropdown */}
      {showNotifications && (
        <div className="fixed top-20 right-6 w-96 glass rounded-xl p-4 glow-box z-50 max-h-96 overflow-y-auto" data-testid="notifications-panel">
          <h3 className="text-xl font-bold text-[#00f0ff] mb-4">Notifications</h3>
          {notifications.length === 0 ? (
            <p className="text-[#00f0ff]/60 text-center py-4">No notifications</p>
          ) : (
            <div className="space-y-2">
              {notifications.map(notif => (
                <div
                  key={notif.notification_id}
                  className={`p-3 rounded-lg border ${notif.read ? 'border-[#00f0ff]/20 bg-[#0a0a1f]/30' : 'border-[#ff00ff] bg-[#ff00ff]/10'}`}
                  data-testid={`notification-${notif.notification_id}`}
                >
                  <p className="font-semibold text-[#00f0ff]">{notif.title}</p>
                  <p className="text-sm text-[#00f0ff]/70">{notif.message}</p>
                  <p className="text-xs text-[#00f0ff]/50 mt-1">
                    {new Date(notif.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-4xl font-bold text-[#00f0ff] glow mb-2">Welcome back, {user?.name}!</h2>
          <p className="text-[#00f0ff]/70">Manage your support tickets and track your tech support requests.</p>
          
          {/* Diagnostic Fee Info Banner */}
          <div className="mt-4 p-4 rounded-xl border-2 border-[#ff00ff] bg-[#ff00ff]/10 glow-box-magenta">
            <p className="text-[#ff00ff] font-bold text-center">
              💳 All services include a $75 diagnostic call fee that applies toward your total service cost!
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="glass rounded-xl p-6 glow-box hover-lift" data-testid="stat-open-tickets">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#00f0ff]/70 text-sm">Open Tickets</p>
                <p className="text-4xl font-bold text-[#00f0ff] glow mt-2">
                  {tickets.filter(t => t.status === 'open').length}
                </p>
              </div>
              <Ticket className="text-[#00f0ff]" size={48} />
            </div>
          </div>
          <div className="glass rounded-xl p-6 glow-box-magenta hover-lift" data-testid="stat-in-progress">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#ff00ff]/70 text-sm">In Progress</p>
                <p className="text-4xl font-bold text-[#ff00ff] glow mt-2">
                  {tickets.filter(t => t.status === 'in_progress').length}
                </p>
              </div>
              <Settings className="text-[#ff00ff] animate-spin" style={{ animationDuration: '3s' }} size={48} />
            </div>
          </div>
          <div className="glass rounded-xl p-6 glow-box-purple hover-lift" data-testid="stat-total-tickets">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#b026ff]/70 text-sm">Total Tickets</p>
                <p className="text-4xl font-bold text-[#b026ff] glow mt-2">{tickets.length}</p>
              </div>
              <Ticket className="text-[#b026ff]" size={48} />
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="mb-8">
          <button
            onClick={() => setShowCreateTicket(true)}
            className="px-8 py-4 btn-primary text-white font-bold rounded-lg flex items-center space-x-2 glow-box hover-lift"
            data-testid="create-ticket-button"
          >
            <Plus size={24} />
            <span>Create New Ticket</span>
          </button>
        </div>

        {/* Review & Referral Section - Show if user has resolved tickets */}
        {tickets.some(t => t.status === 'resolved' || t.status === 'closed') && (
          <div className="mb-8">
            <ReviewReferralCard />
          </div>
        )}

        {/* Tickets List */}
        <div className="glass rounded-xl p-6 glow-box">
          <h3 className="text-2xl font-bold text-[#00f0ff] mb-6">Your Tickets</h3>
          {tickets.length === 0 ? (
            <div className="text-center py-12">
              <Ticket className="mx-auto text-[#00f0ff]/30 mb-4" size={64} />
              <p className="text-[#00f0ff]/60 text-lg">No tickets yet. Create your first support request!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tickets.map(ticket => (
                <div
                  key={ticket.ticket_id}
                  onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                  className="p-4 rounded-lg border border-[#00f0ff]/30 hover:border-[#ff00ff] bg-[#0a0a1f]/50 cursor-pointer hover-lift transition-all"
                  data-testid={`ticket-${ticket.ticket_id}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-[#00f0ff]">{ticket.title}</h4>
                      <p className="text-[#00f0ff]/70 text-sm mt-1">{ticket.service_name}</p>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(ticket.status)}`}>
                        {ticket.status.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className={`text-xs font-semibold ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority.toUpperCase()} PRIORITY
                      </span>
                    </div>
                  </div>
                  <p className="text-[#00f0ff]/60 text-sm line-clamp-2">{ticket.description}</p>
                  <p className="text-[#00f0ff]/40 text-xs mt-2">
                    Created {new Date(ticket.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Ticket Modal */}
      {showCreateTicket && (
        <CreateTicketModal
          services={services}
          onClose={() => setShowCreateTicket(false)}
          onSuccess={() => {
            setShowCreateTicket(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}

export default Dashboard;